import React, { useState, useEffect } from "react";
import { Modal, Table, Button, Form, Select, DatePicker, Input, Typography, Col, Row, Card, Divider, Tag } from "antd"; 
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'; 
import instance from "../utils/axiosInstance";
import moment from "moment";
import dayjs from "dayjs";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Title } = Typography;

const MarketDeclareResult = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  
  const [marketGameList, setMarketGameList] = useState([]);
  const [gameOptions, setGameOptions] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [selectedMarketGame, setSelectedMarketGame] = useState(null);
  const [selectedGameName, setSelectedGameName] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [loadingDeclareResult, setLoadingDeclareResult] = useState(false);
  const [isWinnerModalVisible, setIsWinnerModalVisible] = useState(false);
  
  const [winners, setWinners] = useState({
    openWinners: [],
    closeWinners: [],
    jodiWinners: []
  });
  
  const [gameResults, setGameResults] = useState([]); 
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editingWinner, setEditingWinner] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs()); 
  const [digitValue, setDigitValue] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const pannaOptions = {
    0: ["127", "136", "145", "190", "235", "280", "370", "389", "460", "479", "569", "578", "118", "226", "244", "299", "334", "488", "668", "677", "000", "550"],
    1: ["137", "128", "146", "236", "245", "290", "380", "470", "489", "560", "678", "579", "119", "155", "227", "335", "344", "399", "588", "669", "777", "100"],
    2: ["129", "138", "147", "156", "237", "246", "345", "390", "480", "570", "589", "679", "110", "228", "255", "336", "499", "660", "688", "778", "200", "444"],
    3: ["120", "139", "148", "157", "238", "247", "256", "346", "490", "580", "670", "689", "166", "229", "337", "355", "445", "599", "779", "788", "300", "111"],
    4: ["130", "149", "158", "167", "239", "248", "257", "347", "356", "590", "680", "789", "112", "220", "266", "338", "446", "455", "699", "770", "400", "888"],
    5: ["140", "159", "168", "230", "249", "258", "267", "348", "357", "456", "690", "780", "113", "122", "177", "339", "366", "447", "799", "889", "500", "555"],
    6: ["123", "150", "169", "178", "240", "259", "268", "349", "358", "367", "457", "790", "114", "277", "330", "448", "466", "556", "880", "899", "600", "222"],
    7: ["124", "160", "179", "250", "269", "278", "340", "359", "368", "458", "467", "890", "115", "133", "188", "223", "377", "449", "557", "566", "700", "999"],
    8: ["125", "134", "170", "189", "260", "279", "350", "369", "378", "459", "468", "567", "116", "224", "233", "288", "440", "477", "558", "990", "800", "666"],
    9: ["126", "135", "180", "234", "270", "289", "360", "379", "450", "469", "478", "568", "117", "144", "199", "225", "388", "559", "577", "667", "900", "333"],
  };

  const allPannaNumbers = Object.values(pannaOptions).flat();

  const handlePannaChange = (value) => {
    if (!value) return;
    const sum = value.split("").reduce((acc, num) => acc + parseInt(num, 10), 0);
    const lastDigit = sum % 10;
    setDigitValue(lastDigit.toString());
    form.setFieldsValue({ digit: lastDigit.toString() });
  };

  const fetchMarketGameList = async () => {
    try {
      setLoading(true);
      const response = await instance.get(`/api/marketManagement/getMarketGames`);
      if (response?.data) {
        const uniqueMarkets = [
          ...new Set(
            response.data
              .map((item) => item.marketName)
              .filter((name) => name && name.trim() !== "")
          ),
        ];
        setMarketGameList(uniqueMarkets);
        setAllGames(response.data);
      }
    } catch (error) {
      console.error("Error fetching market list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketGameList();
  }, []);

  // Fetch Previously Declared Results
  const fetchDeclaredResults = async (dateObj) => {
    if (!dateObj) return;
    try {
      setLoading(true);
      const formattedDate = dateObj.format("YYYY-MM-DD");
      const gameResponse = await instance.get(`/api/marketManagement/getMarketGames`);
      const mainMarketGames = gameResponse.data
        .filter((game) => game.marketName === "Main Market")
        .map((game) => game.gameName);

      let results = [];
      try {
        const resultResponse = await instance.get(`/api/mainmarketdeclareResult/getDeclareResult`);
        results = resultResponse?.data?.results || [];
      } catch (err) {
        console.error(err);
      }

      const resultMap = {};
      results.forEach((item) => {
        if (item.marketName === "Main Market" && item.date === formattedDate) {
          const key = `${item.gameName}`;
          if (!resultMap[key]) resultMap[key] = { open: null, close: null, id: item._id };
          
          if (item.gameType === "open") resultMap[key].open = { value: `${item.panna}-${item.digit}`, id: item._id };
          if (item.gameType === "close") resultMap[key].close = { value: `${item.digit}-${item.panna}`, id: item._id };
        }
      });

      const mergedResults = mainMarketGames.map((gameName, index) => ({
        sNo: index + 1,
        gameName,
        date: dateObj.format("DD-MM-YYYY"),
        open: resultMap[gameName]?.open || null,
        close: resultMap[gameName]?.close || null,
      }));

      setGameResults(mergedResults);
      setFilteredResults(mergedResults);
    } catch (error) {
      console.error("Error fetching declared results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) fetchDeclaredResults(selectedDate);
  }, [selectedDate, refresh]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    const filtered = gameResults.filter((item) =>
      item.gameName.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredResults(filtered);
  };

  const fetchWinners = async () => {
    const values = form.getFieldsValue();
    if (!values.marketGame || !values.gameName || !values.gameType || !values.panna) {
      toast.error("Please select all required fields.");
      return;
    }
    try {
      setLoading(true);
      const dateToSend = values.resultDate ? values.resultDate.format("DD-MM-YYYY") : moment().format("DD-MM-YYYY");

      const response = await instance.post(`/api/showwinners/getShowWinnerBids`, {
        marketName: values.marketGame,
        gameName: values.gameName,
        date: dateToSend,
        gameType: values.gameType,
        digit: String(values.digit),
        panna: values.panna,
      });

      setWinners({
        openWinners: response.data.openSessionWins || [],
        jodiWinners: response.data.jodiSessionWins || [], 
        closeWinners: []
      });

      setIsWinnerModalVisible(true);

    } catch (error) {
      console.error("Error fetching winners:", error);
      toast.error("Failed to fetch winner data.");
      setWinners({ openWinners: [], closeWinners: [], jodiWinners: [] });
    } finally {
      setLoading(false);
    }
  };

  // ✅ DIRECT CALL - NO ALERT
  const confirmDeclareResult = () => {
    const values = form.getFieldsValue();
    if (!values.marketGame || !values.gameName || !values.gameType || !values.panna) {
        toast.warning("Please fill all fields before declaring!");
        return;
    }
    declareWinner(); // Direct Action
  };

  const declareWinner = async () => {
    const values = form.getFieldsValue();
    try {
      setLoadingDeclareResult(true);
      const normalizedGameName = values.gameName.trim();
      const declaredDateStr = values.resultDate ? values.resultDate.format("DD-MM-YYYY") : moment().format("DD-MM-YYYY");

      const response = await instance.post(
        `/api/mainmarketdeclareResult/declareResult`,
        {
          marketName: values.marketGame,
          gameName: normalizedGameName,
          date: declaredDateStr,
          gameType: values.gameType,
          digit: values.digit,
          panna: values.panna,
          winners: []
        }
      );

      if (response.data.success === false) {
        toast.error(response.data.message);
      } else {
        toast.success("Result declared successfully!");
        setIsWinnerModalVisible(false);
        setRefresh(prev => !prev);
        
        try {
            const storedToken = localStorage.getItem("fcmToken");
            if (storedToken) {
                await instance.post('/api/notification', {
                  token: storedToken,
                  market: values.marketGame,
                  gameType: values.gameType,
                  gameName: normalizedGameName,
                  result: values.gameType === "open" ? `${values.panna}-${values.digit}` : `${values.digit}-${values.panna}`,
                  declaredAt: new Date().toISOString()
                });
            }
        } catch (e) {}
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to declare winner.");
    } finally {
      setLoadingDeclareResult(false);
    }
  };

  // ✅ DIRECT DELETE - NO ALERT
  const handleDeleteDeclaredResult = async (declaredId) => {
    if (!declaredId) return;
    try {
      await instance.delete(`/api/mainmarketdeclareResult/delete/${declaredId}`);
      toast.success("Result deleted successfully!");
      
      // Live Update State
      setGameResults(prev => prev.map(item => {
          const newItem = { ...item };
          if (newItem.open && newItem.open.id === declaredId) newItem.open = null;
          if (newItem.close && newItem.close.id === declaredId) newItem.close = null;
          return newItem;
      }));
      setRefresh(prev => !prev);
    } catch (error) {
      toast.error("Failed to delete declared result.");
    }
  };

  const handleEdit = (record) => {
    setEditingWinner(record);
    editForm.setFieldsValue({
      points: record.points,
      digit: record.digit,
    });
    setIsEditModalVisible(true);
  };

  // ✅ DIRECT EDIT SAVE - NO ALERT
  const handleSaveEdit = async () => {
    try {
      setIsSavingEdit(true);
      const { points, digit } = editForm.getFieldsValue();
      await instance.put(`/api/bid/updateBid/${editingWinner._id}`, {
        bidId: editingWinner.bidId, newPoints: points, newbidvalue: digit,
      });
      toast.success("Bid updated!");
      setIsEditModalVisible(false);
      fetchWinners();
    } catch (error) {
      toast.error("Failed to update.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ✅ DIRECT DELETE BID - NO ALERT
  const handleDeleteBid = async (record) => {
    try {
      setIsDeleting(true);
      await instance.delete(`/api/bid/deleteBid/${record._id}`, { data: { bidId: record.bidId } });
      toast.success("Bid deleted!");
      
      setWinners(prev => ({
        openWinners: prev.openWinners.filter(w => w._id !== record._id),
        jodiWinners: prev.jodiWinners.filter(w => w._id !== record._id),
        closeWinners: prev.closeWinners.filter(w => w._id !== record._id),
      }));
    } catch (error) {
      toast.error("Failed to delete bid.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMarketChange = (val) => {
    const games = allGames.filter(g => g.marketName === val).map(g => g.gameName);
    setGameOptions([...new Set(games)]);
    setSelectedMarketGame(val);
    form.setFieldsValue({ gameName: undefined });
  };

  const columns = [
    { title: "User Name", dataIndex: "userName" },
    { title: "Game Name", dataIndex: "gameName" },
    { title: "Game Type", dataIndex: "gameType" },
    { title: "Date", dataIndex: "createdAt", render: (text) => moment(text).format("YYYY-MM-DD HH:mm:ss") },
    { title: "Digit/Pana", dataIndex: "digit" },
    { title: "Bid Amount", dataIndex: "points" },
    { 
      title: "Winning Amount / Status", 
      render: (_, record) => {
        if (record.status === "RUNNING") {
          return <Tag color="orange">RUNNING</Tag>;
        }
        return <span style={{ color: "green", fontWeight: "bold" }}>{record.winningPoints}</span>;
      } 
    },
    { 
      title: "Action", 
      render: (_, record) => (
        <div>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{marginRight: 5}}>Edit</Button>
          <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteBid(record)}>Delete</Button>
        </div>
      ) 
    },
  ];

  const resultColumns = [
    { title: "#", dataIndex: "sNo", width: 50 },
    { title: "Game Name", dataIndex: "gameName" },
    { title: "Open Result", render: (_, r) => r.open ? r.open.value : "━━" },
    { title: "Action", render: (_, r) => r.open && <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteDeclaredResult(r.open.id)} /> },
    { title: "Close Result", render: (_, r) => r.close ? r.close.value : "━━" },
    { title: "Action", render: (_, r) => r.close && <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteDeclaredResult(r.close.id)} /> },
  ];

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      <Card title="Declare Result" bordered={false} style={{ margin: "20px" }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="resultDate" label="Date" initialValue={dayjs()}>
                <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="marketGame" label="Market Name" rules={[{ required: true }]}>
                <Select onChange={handleMarketChange} placeholder="Select Market">
                  {marketGameList.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="gameName" label="Game Name" rules={[{ required: true }]}>
                <Select placeholder="Select Game" disabled={!gameOptions.length}>
                  {gameOptions.map(g => <Select.Option key={g} value={g}>{g}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="gameType" label="Session" rules={[{ required: true }]}>
                <Select placeholder="Select Type">
                  <Select.Option value="open">Open</Select.Option>
                  <Select.Option value="close">Close</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="panna" label="Panna" rules={[{ required: true }]}>
                <Select showSearch onChange={handlePannaChange} filterOption={(input, option) => option.children.includes(input)}>
                  {allPannaNumbers.map(p => <Select.Option key={p} value={p}>{p}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="digit" label="Digit">
                <Input value={digitValue} readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} justify="center">
            <Col><Button type="primary" onClick={fetchWinners} style={{ width: 150, marginRight: 10, background: "#faad14" }}>Show Winners</Button></Col>
            <Col><Button type="primary" onClick={confirmDeclareResult} loading={loadingDeclareResult} style={{ width: 150 }}>Declare Result</Button></Col>
          </Row>
        </Form>
      </Card>

      <Modal title="Winning Bids" open={isWinnerModalVisible} onCancel={() => setIsWinnerModalVisible(false)} width="90%" footer={null}>
        {winners.openWinners.length > 0 && (
          <>
            <Title level={5}>Open Session Winners</Title>
            <Table dataSource={winners.openWinners} columns={columns} rowKey="_id" pagination={false} scroll={{ x: true }} />
            <Divider />
          </>
        )}
        
        {winners.jodiWinners.length > 0 && (
          <>
            <Title level={5}>Jodi / Sangam / Running Bids</Title>
            <Table dataSource={winners.jodiWinners} columns={columns} rowKey="_id" pagination={false} scroll={{ x: true }} />
          </>
        )}

        {winners.openWinners.length === 0 && winners.jodiWinners.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "red" }}>No Winners Found</div>
        )}
      </Modal>

      <Modal title="Edit Bid" open={isEditModalVisible} onCancel={() => setIsEditModalVisible(false)} onOk={handleSaveEdit} confirmLoading={isSavingEdit}>
        <Form form={editForm} layout="vertical">
          <Form.Item label="Points" name="points"><Input type="number" /></Form.Item>
          <Form.Item label="Digit" name="digit"><Input /></Form.Item>
        </Form>
      </Modal>

      <Card title="Result History" style={{ margin: "20px" }}>
        <div style={{ marginBottom: 15, display: "flex", justifyContent: "space-between" }}>
          <DatePicker value={selectedDate} onChange={d => setSelectedDate(d)} format="DD-MM-YYYY" allowClear={false} />
          <Input.Search placeholder="Search Game" onSearch={handleSearch} style={{ width: 300 }} />
        </div>
        <Table columns={resultColumns} dataSource={filteredResults} rowKey="gameName" pagination={false} bordered />
      </Card>
    </div>
  );
};

export default MarketDeclareResult;